<script lang="ts" module>
  // Surface / card — the design-system surface primitive
  // (wiki/feature-design-system.md §Component primitives, AC4). A calm paper
  // panel with a hairline border. `bordered={false}` drops the border for the
  // full-bleed pane case (the practice view's editorial layout uses hairline
  // dividers between sections rather than boxed cards).
  //
  // The frame rule lives HERE, not in the discipline of every caller: a Surface
  // publishes "a frame exists here" and one that finds a frame already published
  // renders flat. It has to be a runtime rule — the frames that nest are in
  // different files, each correct alone, so no source check can see it.
  //
  // Forwards arbitrary attributes (`data-testid`, `title`, `aria-labelledby`,
  // …) onto the root via `...rest`, so a host never has to wrap the Surface in a
  // `<div>` just to attach a test hook or an aria reference.
  import type { Snippet } from 'svelte';
  import type { HTMLAttributes } from 'svelte/elements';

  const FRAME_KEY = Symbol('vd-surface-frame');

  interface FrameContext {
    readonly framed: boolean;
  }
</script>

<script lang="ts">
  import { getContext, setContext } from 'svelte';

  interface Props extends HTMLAttributes<HTMLDivElement> {
    /** Render the hairline border (default true). */
    bordered?: boolean;
    /**
     * What to do inside another Surface's frame. `flatten` (default) drops this
     * one's own frame; `keep` draws a second one — for a boundary that carries
     * meaning of its own (a danger-tinted confirm, a grid of peer tiles).
     */
    nested?: 'flatten' | 'keep';
    /** The rendered element (default `div`) — a host keeps its own semantics. */
    as?: string;
    /** Inner padding shorthand (default `1.25rem var(--card-gutter)`). */
    padding?: string;
    /** Extra classes. */
    class?: string;
    children: Snippet;
  }

  let {
    bordered = true,
    nested = 'flatten',
    as = 'div',
    padding = '1.25rem var(--card-gutter)',
    class: klass = '',
    children,
    ...rest
  }: Props = $props();

  const outer = getContext<FrameContext | undefined>(FRAME_KEY);
  const flat = $derived(outer?.framed === true && nested === 'flatten');
  const framed = $derived(bordered && !flat);
  setContext<FrameContext>(FRAME_KEY, {
    get framed() {
      return framed || outer?.framed === true;
    },
  });
</script>

<svelte:element
  this={as}
  {...rest}
  class="vd-surface {klass}"
  class:vd-surface--bordered={framed}
  class:vd-surface--flat={flat}
  style="--vd-surface-pad: {flat ? '0' : padding};"
>
  {@render children()}
</svelte:element>

<style lang="scss">
  .vd-surface {
    background: var(--paper);
    color: var(--ink);
    border-radius: 12px;
    padding: var(--vd-surface-pad);
  }

  .vd-surface--bordered {
    border: 1px solid var(--line);
  }

  // Doubled up so a host's own `:global(.some-card)` rule cannot re-ground a
  // flattened surface by file order alone.
  .vd-surface.vd-surface--flat {
    background: none;
    border-radius: 0;
    border: 0;
  }
</style>
